import { fetchJSON as fetchAPI } from './client';

export type OrderGridRow = {
  id: string;
  amazon_order_id: string;
  marketplace_id?: string;
  status: string;
  channel: string;
  line_count: number;
  item_total: number;
  currency?: string;
  profit_margin?: number;
  purchase_date: string;
  has_open_return: boolean;
  shipment_count: number;
  updated_at: string;
};

export type DashboardSummary = {
  tenant_id: string;
  workspace_id?: string;
  total_orders: number;
  active_orders: number;
  pending_shipment: number;
  shipped_today: number;
  return_count: number;
  cancelled_count: number;
  total_revenue: number;
  avg_order_value: number;
  pending_bulk_jobs: number;
  unread_notifications: number;
  indexed_orders: number;
  by_status?: Record<string, number>;
  by_channel?: Record<string, number>;
  recent_activity?: ActivityEntry[];
  top_orders?: OrderGridRow[];
  generated_at: string;
};

export type Order = {
  id: string;
  workspace_id: string;
  identifiers: { amazon_order_id: string; marketplace_order_id?: string; seller_order_id?: string };
  marketplace_id?: string;
  status: string;
  line_items: { id: string; sku?: string; asin?: string; title?: string; quantities: { ordered: number; shipped: number } }[];
  fulfillment?: { channel: string; shipments?: { shipment_id: string; carrier?: string; tracking_number?: string }[] };
  shipping?: { region?: string; postal_code?: string; country?: string };
  financials?: { item_total: number; shipping_charge?: number; fees?: number; currency?: string };
  purchase_date: string;
  updated_at: string;
};

export type SearchResult = {
  orders: Order[];
  total: number;
  limit: number;
  offset: number;
  facets?: {
    status?: Record<string, number>;
    channel?: Record<string, number>;
    marketplace?: Record<string, number>;
  };
  took_ms?: number;
};

export type ActivityEntry = {
  id: string;
  order_id?: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type OrderNotification = {
  id: string;
  order_id?: string;
  title: string;
  body?: string;
  priority: string;
  read: boolean;
  created_at: string;
};

export type AIRecommendation = {
  id: string;
  order_id: string;
  type: string;
  title: string;
  description: string;
  impact: number;
  confidence: number;
  field?: string;
};

export type OrderProfitability = {
  order_id: string;
  revenue: number;
  cogs: number;
  fees: number;
  net_profit: number;
  margin_percent: number;
  currency?: string;
};

export type ShipmentCenterSummary = {
  total_shipments: number;
  in_transit: number;
  delivered: number;
  delayed: number;
  recent_shipments?: {
    order_id: string;
    amazon_order_id?: string;
    shipment_id: string;
    carrier?: string;
    tracking_number?: string;
    status: string;
  }[];
};

export type ReturnsRefundCenter = {
  open_returns: number;
  completed_returns: number;
  pending_refunds: number;
  total_refund_value: number;
  by_status?: Record<string, number>;
};

export type BulkOperation = {
  id: string;
  action: string;
  state: string;
  order_ids: string[];
  progress: number;
  processed: number;
  failed: number;
  total: number;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_AMAZON_ORDERS_API ?? '/api/v1/amazon/orders';

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const amazonOrdersApi = {
  dashboard: (workspaceId?: string) =>
    fetchJSON<DashboardSummary>(`/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  ordersGrid: (workspaceId?: string, limit = 50, offset = 0) =>
    fetchJSON<{ rows: OrderGridRow[]; total: number }>(
      `/grid?workspace_id=${workspaceId ?? ''}&limit=${limit}&offset=${offset}`,
    ),
  globalSearch: (text: string, workspaceId?: string) =>
    fetchJSON<SearchResult>('/search/global', {
      method: 'POST',
      body: JSON.stringify({ text, workspace_id: workspaceId ?? '', limit: 50 }),
    }),
  advancedSearch: (query: Record<string, unknown>) =>
    fetchJSON<SearchResult>('/search/advanced', { method: 'POST', body: JSON.stringify(query) }),
  getOrder: (id: string) => fetchJSON<Order>(`/${id}`),
  profitability: (id: string) => fetchJSON<OrderProfitability>(`/${id}/profitability`),
  recommendations: (id: string) => fetchJSON<AIRecommendation[]>(`/${id}/recommendations`),
  shipmentCenter: (workspaceId?: string) =>
    fetchJSON<ShipmentCenterSummary>(`/shipments?workspace_id=${workspaceId ?? ''}`),
  returnsCenter: (workspaceId?: string) =>
    fetchJSON<ReturnsRefundCenter>(`/returns-center?workspace_id=${workspaceId ?? ''}`),
  triggerBulk: (body: { action: string; order_ids: string[]; workspace_id: string; parameters?: Record<string, unknown> }) =>
    fetchJSON<BulkOperation>('/bulk', { method: 'POST', body: JSON.stringify(body) }),
  listBulk: (workspaceId?: string) =>
    fetchJSON<BulkOperation[]>(`/bulk?workspace_id=${workspaceId ?? ''}`),
  listActivity: (orderId?: string, limit = 50) =>
    fetchJSON<ActivityEntry[]>(`/activity${orderId ? `?order_id=${orderId}&limit=${limit}` : `?limit=${limit}`}`),
  listNotifications: (unread = false) =>
    fetchJSON<OrderNotification[]>(`/notifications?unread=${unread}`),
  adminHealth: () => fetchJSON<Record<string, unknown>>('/admin/health'),
  diagnostics: () => fetchJSON<Record<string, unknown>>('/diagnostics'),
  rebuildIndex: () => fetchJSON<{ indexed: number }>('/index/rebuild', { method: 'POST' }),
};

export const BULK_ACTIONS = ['recalculate_profitability', 'sync', 'archive', 'update_status'] as const;
export const ORDER_STATUSES = ['pending', 'unshipped', 'partially_shipped', 'shipped', 'delivered', 'cancelled', 'returned', 'refunded'] as const;
